export type IdlePolicyOptions = {
  onIdle(): void;
  onActive(): void;
  /** Hidden tab for this long → idle (spec §7.5: 5 min). */
  hiddenMs?: number;
  /** No interaction for this long → idle (15 min). */
  idleMs?: number;
  now?: () => number;
  /** Starts a timer; returns its cancel function. */
  setTimer?: (fn: () => void, ms: number) => () => void;
};

const INTERACTIONS = ['pointerdown', 'pointermove', 'keydown', 'wheel', 'touchstart'] as const;

/**
 * Voluntary disconnect of an unattended tab: calls `onIdle` once, then `onActive` on the next sign of life.
 * Interactions only record a timestamp (pointermove is hot); a single timer checks it.
 */
export function createIdlePolicy(opts: IdlePolicyOptions): { destroy(): void } {
  const hiddenMs = opts.hiddenMs ?? 300_000;
  const idleMs = opts.idleMs ?? 900_000;
  const now = opts.now ?? (() => Date.now());
  const setTimer =
    opts.setTimer ??
    ((fn: () => void, ms: number) => {
      const h = setTimeout(fn, ms);
      return () => clearTimeout(h);
    });

  let idle = false;
  let lastActive = now();
  let cancelIdle: (() => void) | null = null;
  let cancelHidden: (() => void) | null = null;

  const goIdle = () => {
    if (idle) return;
    idle = true;
    opts.onIdle();
  };
  const armIdle = (ms: number) => {
    cancelIdle?.();
    cancelIdle = setTimer(() => {
      const left = lastActive + idleMs - now();
      if (left > 0) armIdle(left);
      else goIdle();
    }, ms);
  };
  const wake = () => {
    lastActive = now();
    if (!idle) return;
    idle = false;
    armIdle(idleMs);
    opts.onActive();
  };
  const onVisibility = () => {
    cancelHidden?.();
    cancelHidden = null;
    if (document.visibilityState === 'hidden') cancelHidden = setTimer(goIdle, hiddenMs);
    else wake();
  };

  armIdle(idleMs);
  document.addEventListener('visibilitychange', onVisibility);
  for (const e of INTERACTIONS) window.addEventListener(e, wake, { passive: true });
  return {
    destroy() {
      cancelIdle?.();
      cancelHidden?.();
      document.removeEventListener('visibilitychange', onVisibility);
      for (const e of INTERACTIONS) window.removeEventListener(e, wake);
    },
  };
}
